import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { FaCalendarDays, FaPenToSquare, FaPerson } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import Select from 'react-select';

import { userAppData } from '../MainApp.js';
import {
    SEC_ApiCall,
    SEC_DataApiPost,
    getProfileImageUrlWithSchoolFallback,
} from '../func.js';
import { ApiError } from '../common/apiError.js';
import Header from '../common/header.js';
import PolarSwitch from '../common/formComponents/polarSwitch.js';
import { FaArrowLeft } from 'react-icons/fa';

import './SysManagerBookings.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const GROUP_ACCESS_OPTIONS = [
    { value: 'none', label: 'No permission' },
    { value: 'view', label: 'View' },
    { value: 'edit', label: 'Edit' },
    { value: 'manage', label: 'Manage' },
];
const STAFF_PERMISSION_FILTER_OPTIONS = [
    { value: 'all', label: 'All permissions' },
    ...GROUP_ACCESS_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
    })),
];
const ATTENDEE_FILTER_OPTIONS = [
    { value: 'all', label: 'All attendee states' },
    { value: 'enabled', label: 'Attendees allowed' },
    { value: 'disabled', label: 'Attendees not allowed' },
];
const DISPLAY_SLOT_OPTIONS = [
    { value: '5', label: '5 mins' },
    { value: '10', label: '10 mins' },
    { value: '15', label: '15 mins' },
];
const VALID_GROUP_ACCESS_LEVELS = new Set(
    GROUP_ACCESS_OPTIONS
        .map((option) => option.value)
        .filter((value) => value !== 'none')
);

function hasBookingsAdminAccess() {
    const sysManagerPerms = userAppData?.sysManager || userAppData?.sysManagerData || {};
    return sysManagerPerms.perms === 1 && sysManagerPerms.bookingsAdmin === true;
}

function parseMetadataResponse(response) {
    const rawMetadata = response?.metadata ?? response?.item ?? response;
    if (typeof rawMetadata === 'string') {
        try {
            return {
                metadata: normaliseMetadata(JSON.parse(rawMetadata)),
                staffAccessItems: normaliseStaffAccessItems(response?.staffAccessItems),
            };
        } catch (error) {
            console.error('Failed to parse bookings metadata JSON', error);
            return {
                metadata: normaliseMetadata({}),
                staffAccessItems: normaliseStaffAccessItems(response?.staffAccessItems),
            };
        }
    }

    return {
        metadata: normaliseMetadata(rawMetadata),
        staffAccessItems: normaliseStaffAccessItems(response?.staffAccessItems),
    };
}

function normaliseMetadata(metadata) {
    const cleanMetadata = metadata && typeof metadata === 'object' ? metadata : {};
    return {
        groups: Array.isArray(cleanMetadata.groups) ? cleanMetadata.groups : [],
        subGroups: Array.isArray(cleanMetadata.subGroups) ? cleanMetadata.subGroups : [],
        rooms: Array.isArray(cleanMetadata.rooms) ? cleanMetadata.rooms : [],
    };
}

function buildNameLookup(items, idKey) {
    return items.reduce((lookup, item) => {
        const id = String(item?.[idKey] || '').trim();
        const name = String(item?.name || '').trim();
        if (id && name) {
            lookup[id] = name;
        }
        return lookup;
    }, {});
}

function normaliseStaffAccessItems(items) {
    if (!Array.isArray(items)) {
        return [];
    }

    return items
        .map((item) => {
            const cogId = String(item?.cogId || '').trim();
            const displayName = String(item?.displayName || '').trim();
            const groupAccess = Array.isArray(item?.groupAccess) ? item.groupAccess : [];

            return {
                cogId,
                displayName,
                profileImageUrl: String(item?.profileImageUrl || '').trim(),
                bookingsPerms: Number(item?.bookingsPerms || 0),
                groupAccess: groupAccess
                    .map((accessEntry) => ({
                        id: String(accessEntry?.id || '').trim(),
                        perms: String(accessEntry?.perms || '').trim().toLowerCase(),
                        allowAttendees: Boolean(accessEntry?.allowAttendees),
                    }))
                    .filter((accessEntry) => accessEntry.id),
            };
        })
        .filter((item) => item.cogId && item.displayName);
}

function createManageAccessState() {
    return {
        open: false,
        group: null,
        searchText: '',
        permissionFilter: 'all',
        attendeesFilter: 'all',
    };
}

function createEditGroupState() {
    return {
        open: false,
        groupId: '',
        name: '',
        type: '',
        displaySlotMins: '',
        hasDisplaySlotMins: false,
        saving: false,
    };
}

function createEditSubGroupState() {
    return {
        open: false,
        groupId: '',
        name: '',
        saving: false,
    };
}

function createEditRoomState() {
    return {
        open: false,
        roomId: '',
        name: '',
        groupOption: null,
        subGroupOption: null,
        notes: '',
        bookable: false,
        allowNotes: false,
        allowAttendees: false,
        saving: false,
    };
}

function getAvatarInitial(name) {
    const cleanName = String(name || '').trim();
    return cleanName ? cleanName.charAt(0).toUpperCase() : '?';
}

function getGroupAccessLevel(staffMember, groupId) {
    const cleanGroupId = String(groupId || '').trim();
    const matchingAccess = (staffMember?.groupAccess || []).find((accessEntry) => (
        String(accessEntry?.id || '').trim() === cleanGroupId
    ));
    const accessLevel = String(matchingAccess?.perms || 'none').trim().toLowerCase();
    return VALID_GROUP_ACCESS_LEVELS.has(accessLevel) ? accessLevel : 'none';
}

function getGroupAllowAttendees(staffMember, groupId) {
    const cleanGroupId = String(groupId || '').trim();
    const matchingAccess = (staffMember?.groupAccess || []).find((accessEntry) => (
        String(accessEntry?.id || '').trim() === cleanGroupId
    ));
    return Boolean(matchingAccess?.allowAttendees);
}

function replaceGroupAccessLevel(staffMember, groupId, nextAccessLevel, nextAllowAttendees = false) {
    const cleanGroupId = String(groupId || '').trim();
    const cleanAccessLevel = String(nextAccessLevel || 'none').trim().toLowerCase();
    const preservedAccess = (staffMember?.groupAccess || []).filter((accessEntry) => (
        String(accessEntry?.id || '').trim() !== cleanGroupId
    ));

    if (VALID_GROUP_ACCESS_LEVELS.has(cleanAccessLevel)) {
        preservedAccess.push({
            id: cleanGroupId,
            perms: cleanAccessLevel,
            allowAttendees: Boolean(nextAllowAttendees),
        });
    }

    return {
        ...staffMember,
        bookingsPerms: cleanAccessLevel === 'none' ? staffMember.bookingsPerms : 1,
        groupAccess: preservedAccess,
    };
}

function formatBoolean(value) {
    return value === true ? 'Yes' : 'No';
}

function formatType(value) {
    return String(value || 'Not set').trim();
}

function formatDisplaySlotMins(value) {
    const cleanValue = String(value ?? '').trim();
    return cleanValue ? `${cleanValue} mins` : 'Not set';
}

function formatQuestions(questions) {
    if (!Array.isArray(questions) || questions.length === 0) {
        return 'None';
    }

    return questions
        .map((question) => (
            String(question?.questionShort || question?.questionText || '').trim()
        ))
        .filter(Boolean)
        .join(', ') || `${questions.length} configured`;
}

function isTimeslotGroup(group) {
    return String(group?.type || '').trim().toUpperCase() === 'TIMESLOT';
}

export default function SysManagerBookings() {
    const navigate = useNavigate();
    const [metadata, setMetadata] = useState({ groups: [], subGroups: [], rooms: [] });
    const [staffAccessItems, setStaffAccessItems] = useState([]);
    const [manageAccessState, setManageAccessState] = useState(createManageAccessState);
    const [editGroupState, setEditGroupState] = useState(createEditGroupState);
    const [editSubGroupState, setEditSubGroupState] = useState(createEditSubGroupState);
    const [editRoomState, setEditRoomState] = useState(createEditRoomState);
    const [updatingCogIds, setUpdatingCogIds] = useState({});
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);

    const groupNameLookup = useMemo(
        () => buildNameLookup(metadata.groups, 'groupId'),
        [metadata.groups]
    );
    const subGroupNameLookup = useMemo(
        () => buildNameLookup(metadata.subGroups, 'groupId'),
        [metadata.subGroups]
    );
    const groupSelectOptions = useMemo(
        () => (metadata.groups || []).map((group) => ({
            value: String(group?.groupId || '').trim(),
            label: String(group?.name || 'Unnamed group').trim(),
        })).filter((option) => option.value),
        [metadata.groups]
    );
    const subGroupSelectOptions = useMemo(
        () => [
            { value: '', label: 'No sub group' },
            ...(metadata.subGroups || []).map((subGroup) => ({
                value: String(subGroup?.groupId || '').trim(),
                label: String(subGroup?.name || 'Unnamed sub group').trim(),
            })).filter((option) => option.value),
        ],
        [metadata.subGroups]
    );
    const subGroupRoomCountLookup = useMemo(
        () => (metadata.rooms || []).reduce((lookup, room) => {
            const subGroupId = String(room?.subGroupId || '').trim();
            if (!subGroupId) {
                return lookup;
            }

            return {
                ...lookup,
                [subGroupId]: (lookup[subGroupId] || 0) + 1,
            };
        }, {}),
        [metadata.rooms]
    );
    const selectedGroupAllowsAttendees = useMemo(
        () => isTimeslotGroup(manageAccessState?.group),
        [manageAccessState?.group]
    );
    const filteredStaffAccessItems = useMemo(() => {
        const searchText = String(manageAccessState.searchText || '').trim().toLowerCase();
        return staffAccessItems.filter((staffMember) => (
            String(staffMember?.displayName || '').toLowerCase().includes(searchText)
        )).filter((staffMember) => {
            const currentAccessLevel = getGroupAccessLevel(
                staffMember,
                manageAccessState?.group?.groupId
            );
            const currentAllowAttendees = getGroupAllowAttendees(
                staffMember,
                manageAccessState?.group?.groupId
            );

            if (
                manageAccessState.permissionFilter !== 'all'
                && currentAccessLevel !== manageAccessState.permissionFilter
            ) {
                return false;
            }

            if (!selectedGroupAllowsAttendees || manageAccessState.attendeesFilter === 'all') {
                return true;
            }

            if (manageAccessState.attendeesFilter === 'enabled') {
                return currentAccessLevel !== 'none' && currentAllowAttendees === true;
            }

            if (manageAccessState.attendeesFilter === 'disabled') {
                return currentAllowAttendees === false;
            }

            return true;
        });
    }, [
        manageAccessState.attendeesFilter,
        manageAccessState.group,
        manageAccessState.permissionFilter,
        manageAccessState.searchText,
        selectedGroupAllowsAttendees,
        staffAccessItems,
    ]);

    useEffect(() => {
        if (!hasBookingsAdminAccess()) {
            navigate('/app/sysmanager');
            return;
        }

        async function loadBookingsMetadata() {
            setLoading(true);
            setApiError(false);

            try {
                const response = await SEC_ApiCall(
                    'sysmanager/bookings/metadata',
                    {},
                    setApiError
                );
                const parsedResponse = parseMetadataResponse(response);
                setMetadata(parsedResponse.metadata);
                setStaffAccessItems(parsedResponse.staffAccessItems);
            } catch (error) {
                console.error('Failed to load System Manager bookings metadata', error);
                setApiError(true);
            } finally {
                setLoading(false);
            }
        }

        loadBookingsMetadata();
    }, [navigate]);

    function closeManageAccessModal() {
        setManageAccessState(createManageAccessState());
        setUpdatingCogIds({});
    }

    function openManageAccessModal(group) {
        setManageAccessState({
            ...createManageAccessState(),
            open: true,
            group,
        });
        setUpdatingCogIds({});
    }

    function closeEditGroupModal() {
        setEditGroupState(createEditGroupState());
    }

    function openEditGroupModal(group) {
        setEditGroupState({
            open: true,
            groupId: String(group?.groupId || '').trim(),
            name: String(group?.name || '').trim(),
            type: String(group?.type || '').trim(),
            displaySlotMins: String(group?.displaySlotMins ?? '').trim(),
            hasDisplaySlotMins: Object.prototype.hasOwnProperty.call(group || {}, 'displaySlotMins'),
            saving: false,
        });
    }

    function closeEditSubGroupModal() {
        setEditSubGroupState(createEditSubGroupState());
    }

    function openEditSubGroupModal(subGroup) {
        setEditSubGroupState({
            open: true,
            groupId: String(subGroup?.groupId || '').trim(),
            name: String(subGroup?.name || '').trim(),
            saving: false,
        });
    }

    function closeEditRoomModal() {
        setEditRoomState(createEditRoomState());
    }

    function openEditRoomModal(room) {
        const selectedGroupId = String(room?.groupId || '').trim();
        const selectedSubGroupId = String(room?.subGroupId || '').trim();

        setEditRoomState({
            open: true,
            roomId: String(room?.roomId || '').trim(),
            name: String(room?.name || '').trim(),
            groupOption: groupSelectOptions.find((option) => option.value === selectedGroupId) || null,
            subGroupOption: subGroupSelectOptions.find((option) => option.value === selectedSubGroupId) || subGroupSelectOptions[0] || null,
            notes: String(room?.notes || ''),
            bookable: Boolean(room?.bookable),
            allowNotes: Boolean(room?.allowNotes),
            allowAttendees: Boolean(room?.allowAttendees),
            saving: false,
        });
    }

    function setStaffUpdatingState(targetCogId, stateKey, isUpdating) {
        setUpdatingCogIds((currentValue) => {
            const currentState = currentValue[targetCogId] || {};
            const nextState = {
                ...currentState,
                [stateKey]: isUpdating,
            };

            if (!nextState.access && !nextState.attendees) {
                const nextValue = { ...currentValue };
                delete nextValue[targetCogId];
                return nextValue;
            }

            return {
                ...currentValue,
                [targetCogId]: nextState,
            };
        });
    }

    async function saveGroupAccessChange(staffMember, nextAccessLevel, nextAllowAttendees) {
        const targetCogId = String(staffMember?.cogId || '').trim();
        const displayName = String(staffMember?.displayName || 'this user').trim();
        const groupId = String(manageAccessState?.group?.groupId || '').trim();
        const cleanAccessLevel = String(nextAccessLevel || 'none').trim().toLowerCase();
        const allowAttendees = selectedGroupAllowsAttendees && VALID_GROUP_ACCESS_LEVELS.has(cleanAccessLevel)
            ? Boolean(nextAllowAttendees)
            : false;

        if (!targetCogId || !groupId) {
            toast.error(`❌ Unable to update permissions for ${displayName}.`, TOAST_CONFIG);
            return;
        }

        setStaffUpdatingState(targetCogId, 'access', true);

        try {
            const response = await SEC_DataApiPost(
                'sysmanager/bookings/perms/groups',
                JSON.stringify({
                    targetCogId,
                    groupId,
                    accessLevel: cleanAccessLevel,
                    allowAttendees,
                }),
                {}
            );
            const savedAccessLevel = String(response?.accessLevel || cleanAccessLevel).trim().toLowerCase();
            const savedAllowAttendees = Boolean(response?.allowAttendees);

            setStaffAccessItems((currentItems) => (
                currentItems.map((item) => (
                    item.cogId === targetCogId
                        ? replaceGroupAccessLevel(item, groupId, savedAccessLevel, savedAllowAttendees)
                        : item
                ))
            ));

            toast.success(`✅ Successfully updated permissions for ${displayName}`, TOAST_CONFIG);
        } catch (error) {
            console.error('Failed to update booking group permissions', error);
            toast.error(`❌ ${error?.message || 'Unable to update booking group permissions.'}`, TOAST_CONFIG);
        } finally {
            setStaffUpdatingState(targetCogId, 'access', false);
        }
    }

    async function handleGroupAccessChange(staffMember, nextAccessLevel) {
        const currentAllowAttendees = getGroupAllowAttendees(
            staffMember,
            manageAccessState?.group?.groupId
        );
        await saveGroupAccessChange(staffMember, nextAccessLevel, currentAllowAttendees);
    }

    async function handleGroupAllowAttendeesChange(staffMember, nextAllowAttendees) {
        const targetCogId = String(staffMember?.cogId || '').trim();
        const displayName = String(staffMember?.displayName || 'this user').trim();
        const groupId = String(manageAccessState?.group?.groupId || '').trim();

        if (!targetCogId || !groupId) {
            toast.error(`❌ Unable to update attendee permissions for ${displayName}.`, TOAST_CONFIG);
            return;
        }

        setStaffUpdatingState(targetCogId, 'attendees', true);

        try {
            const response = await SEC_DataApiPost(
                'sysmanager/bookings/perms/allowAttendees',
                JSON.stringify({
                    targetCogId,
                    groupId,
                    allowAttendees: Boolean(nextAllowAttendees),
                }),
                {}
            );
            const savedAllowAttendees = Boolean(response?.allowAttendees);

            setStaffAccessItems((currentItems) => (
                currentItems.map((item) => {
                    if (item.cogId !== targetCogId) {
                        return item;
                    }

                    const currentAccessLevel = getGroupAccessLevel(item, groupId);
                    return replaceGroupAccessLevel(
                        item,
                        groupId,
                        currentAccessLevel,
                        savedAllowAttendees
                    );
                })
            ));

            toast.success(`✅ Successfully updated attendee permissions for ${displayName}`, TOAST_CONFIG);
        } catch (error) {
            console.error('Failed to update allow attendees', error);
            toast.error(`❌ ${error?.message || 'Unable to update attendee permissions.'}`, TOAST_CONFIG);
        } finally {
            setStaffUpdatingState(targetCogId, 'attendees', false);
        }
    }

    async function handleEditGroupSave() {
        const groupId = String(editGroupState.groupId || '').trim();
        const name = String(editGroupState.name || '').trim();
        const type = String(editGroupState.type || '').trim().toUpperCase();

        if (!groupId || !name) {
            toast.error('❌ Group name is required.', TOAST_CONFIG);
            return;
        }

        setEditGroupState((currentValue) => ({
            ...currentValue,
            saving: true,
        }));

        try {
            const payload = {
                groupId,
                name,
                type,
            };

            if (editGroupState.hasDisplaySlotMins) {
                payload.displaySlotMins = Number(editGroupState.displaySlotMins);
            }

            const response = await SEC_DataApiPost(
                'sysmanager/bookings/groups/edit',
                JSON.stringify(payload),
                {}
            );

            const updatedGroup = response?.group || {};
            setMetadata((currentValue) => ({
                ...currentValue,
                groups: (currentValue.groups || []).map((group) => (
                    String(group?.groupId || '').trim() === groupId
                        ? {
                            ...group,
                            ...updatedGroup,
                            name: String(updatedGroup?.name || name).trim(),
                            type: String(updatedGroup?.type || type).trim(),
                        }
                        : group
                )),
            }));

            toast.success(`✅ Successfully updated ${name}`, TOAST_CONFIG);
            closeEditGroupModal();
        } catch (error) {
            console.error('Failed to edit booking group', error);
            toast.error(`❌ ${error?.message || 'Unable to update booking group.'}`, TOAST_CONFIG);
            setEditGroupState((currentValue) => ({
                ...currentValue,
                saving: false,
            }));
        }
    }

    async function handleEditSubGroupSave() {
        const groupId = String(editSubGroupState.groupId || '').trim();
        const name = String(editSubGroupState.name || '').trim();

        if (!groupId || !name) {
            toast.error('❌ Sub group name is required.', TOAST_CONFIG);
            return;
        }

        setEditSubGroupState((currentValue) => ({
            ...currentValue,
            saving: true,
        }));

        try {
            const response = await SEC_DataApiPost(
                'sysmanager/bookings/subGroups/edit',
                JSON.stringify({
                    groupId,
                    name,
                }),
                {}
            );

            const updatedSubGroup = response?.subGroup || {};
            setMetadata((currentValue) => ({
                ...currentValue,
                subGroups: (currentValue.subGroups || []).map((subGroup) => (
                    String(subGroup?.groupId || '').trim() === groupId
                        ? {
                            ...subGroup,
                            ...updatedSubGroup,
                            name: String(updatedSubGroup?.name || name).trim(),
                        }
                        : subGroup
                )),
            }));

            toast.success(`✅ Successfully updated ${name}`, TOAST_CONFIG);
            closeEditSubGroupModal();
        } catch (error) {
            console.error('Failed to edit booking sub group', error);
            toast.error(`❌ ${error?.message || 'Unable to update booking sub group.'}`, TOAST_CONFIG);
            setEditSubGroupState((currentValue) => ({
                ...currentValue,
                saving: false,
            }));
        }
    }

    async function handleEditRoomSave() {
        const roomId = String(editRoomState.roomId || '').trim();
        const name = String(editRoomState.name || '').trim();
        const groupId = String(editRoomState.groupOption?.value || '').trim();
        const subGroupId = String(editRoomState.subGroupOption?.value || '').trim();

        if (!roomId || !name || !groupId) {
            toast.error('❌ Room name and group are required.', TOAST_CONFIG);
            return;
        }

        setEditRoomState((currentValue) => ({
            ...currentValue,
            saving: true,
        }));

        try {
            const response = await SEC_DataApiPost(
                'sysmanager/bookings/rooms/edit',
                JSON.stringify({
                    roomId,
                    name,
                    groupId,
                    subGroupId,
                    notes: editRoomState.notes,
                    bookable: editRoomState.bookable,
                    allowNotes: editRoomState.allowNotes,
                    allowAttendees: editRoomState.allowAttendees,
                }),
                {}
            );

            const updatedRoom = response?.room || {};
            setMetadata((currentValue) => ({
                ...currentValue,
                rooms: (currentValue.rooms || []).map((room) => (
                    String(room?.roomId || '').trim() === roomId
                        ? {
                            ...room,
                            ...updatedRoom,
                            name: String(updatedRoom?.name || name).trim(),
                            groupId: String(updatedRoom?.groupId || groupId).trim(),
                            subGroupId: String(updatedRoom?.subGroupId ?? subGroupId).trim(),
                            notes: String(updatedRoom?.notes ?? editRoomState.notes),
                            bookable: Boolean(updatedRoom?.bookable ?? editRoomState.bookable),
                            allowNotes: Boolean(updatedRoom?.allowNotes ?? editRoomState.allowNotes),
                            allowAttendees: Boolean(updatedRoom?.allowAttendees ?? editRoomState.allowAttendees),
                        }
                        : room
                )),
            }));

            toast.success(`✅ Successfully updated ${name}`, TOAST_CONFIG);
            closeEditRoomModal();
        } catch (error) {
            console.error('Failed to edit booking room', error);
            toast.error(`❌ ${error?.message || 'Unable to update booking room.'}`, TOAST_CONFIG);
            setEditRoomState((currentValue) => ({
                ...currentValue,
                saving: false,
            }));
        }
    }

    if (apiError) {
        return <ApiError />;
    }

    return (
        <div className='main_div'>
            <div className='internal_main_div sysManager_bookings_container'>
                <Header
                    title='Bookings'
                    subtitle='Manage the groups, sub groups and rooms used by the bookings modules.'
                    rightContent={(
                        <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager')}>
                            <FaArrowLeft aria-hidden='true' />
                            Back to System Manager
                        </button>
                    )}
                />

                {loading ? (
                    <div className='sysManager_bookings_loading'>
                        <CgSpinner className='sysManager_bookings_spinner' />
                        <p>Loading bookings metadata...</p>
                    </div>
                ) : (
                    <>
                        <section className='sysManager_bookings_tableCard'>
                            <div className='sysManager_bookings_toolbar'>
                                <div>
                                    <h2 className='sysManager_bookings_cardTitle'>Timetable Masters</h2>
                                    <p className='sysManager_bookings_count'>
                                        Create and edit single-week or multi-week room timetables.
                                    </p>
                                </div>
                                <button
                                    className='sysManager_bookings_primaryActionButton'
                                    type='button'
                                    onClick={() => navigate('/app/sysmanager/bookings/timetables')}
                                >
                                    <FaCalendarDays aria-hidden='true' />
                                    Manage timetable masters
                                </button>
                            </div>
                        </section>

                        <section className='sysManager_bookings_tableCard'>
                            <div className='sysManager_bookings_toolbar'>
                                <div>
                                    <h2 className='sysManager_bookings_cardTitle'>Groups</h2>
                                    <p className='sysManager_bookings_count'>
                                        {metadata.groups.length} groups
                                    </p>
                                </div>
                            </div>
                            {metadata.groups.length === 0 ? (
                                <div className='sysManager_bookings_emptyState'>
                                    No booking groups are configured.
                                </div>
                            ) : (
                                <div className='sysManager_bookings_tableWrap'>
                                    <table className='sysManager_bookings_table'>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Type</th>
                                                <th>Display Slot</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metadata.groups.map((group) => (
                                                <tr key={group.groupId || group.name}>
                                                    <td>{group.name || 'Unnamed group'}</td>
                                                    <td>{formatType(group.type)}</td>
                                                    <td>{formatDisplaySlotMins(group.displaySlotMins)}</td>
                                                    <td>
                                                        <div className='sysManager_bookings_actionButtons'>
                                                            <button
                                                                className='sysManager_bookings_iconActionButton'
                                                                type='button'
                                                                aria-label={`Edit ${group.name || 'booking group'}`}
                                                                title='Edit group'
                                                                onClick={() => openEditGroupModal(group)}
                                                            >
                                                                <FaPenToSquare size={16} />
                                                            </button>
                                                            <button
                                                                className='sysManager_bookings_iconActionButton'
                                                                type='button'
                                                                aria-label={`Manage access for ${group.name || 'booking group'}`}
                                                                title='Manage access'
                                                                onClick={() => openManageAccessModal(group)}
                                                            >
                                                                <FaPerson size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section className='sysManager_bookings_tableCard'>
                            <div className='sysManager_bookings_toolbar'>
                                <div>
                                    <h2 className='sysManager_bookings_cardTitle'>Sub Groups</h2>
                                    <p className='sysManager_bookings_count'>
                                        {metadata.subGroups.length} sub groups
                                    </p>
                                </div>
                            </div>
                            {metadata.subGroups.length === 0 ? (
                                <div className='sysManager_bookings_emptyState'>
                                    No booking sub groups are configured.
                                </div>
                            ) : (
                                <div className='sysManager_bookings_tableWrap'>
                                    <table className='sysManager_bookings_table'>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Rooms</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metadata.subGroups.map((subGroup) => (
                                                <tr key={subGroup.groupId || subGroup.name}>
                                                    <td>{subGroup.name || 'Unnamed sub group'}</td>
                                                    <td>{subGroupRoomCountLookup[subGroup.groupId] || 0}</td>
                                                    <td>
                                                        <div className='sysManager_bookings_actionButtons'>
                                                            <button
                                                                className='sysManager_bookings_iconActionButton'
                                                                type='button'
                                                                aria-label={`Edit ${subGroup.name || 'booking sub group'}`}
                                                                title='Edit sub group'
                                                                onClick={() => openEditSubGroupModal(subGroup)}
                                                            >
                                                                <FaPenToSquare size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section className='sysManager_bookings_tableCard'>
                            <div className='sysManager_bookings_toolbar'>
                                <div>
                                    <h2 className='sysManager_bookings_cardTitle'>Rooms</h2>
                                    <p className='sysManager_bookings_count'>
                                        {metadata.rooms.length} rooms
                                    </p>
                                </div>
                            </div>
                            {metadata.rooms.length === 0 ? (
                                <div className='sysManager_bookings_emptyState'>
                                    No booking rooms are configured.
                                </div>
                            ) : (
                                <div className='sysManager_bookings_tableWrap'>
                                    <table className='sysManager_bookings_table sysManager_bookings_roomsTable'>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Group</th>
                                                <th>Sub Group</th>
                                                <th>Notes</th>
                                                <th>Bookable</th>
                                                <th>Allow Notes</th>
                                                <th>Allow Attendees</th>
                                                <th>Questions</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metadata.rooms.map((room) => (
                                                <tr key={room.roomId || room.name}>
                                                    <td>{room.name || 'Unnamed room'}</td>
                                                    <td>{groupNameLookup[room.groupId] || 'Not set'}</td>
                                                    <td>{subGroupNameLookup[room.subGroupId] || 'None'}</td>
                                                    <td>{room.notes || 'None'}</td>
                                                    <td>{formatBoolean(room.bookable)}</td>
                                                    <td>{formatBoolean(room.allowNotes)}</td>
                                                    <td>{formatBoolean(room.allowAttendees)}</td>
                                                    <td>{formatQuestions(room.questions)}</td>
                                                    <td>
                                                        <div className='sysManager_bookings_actionButtons'>
                                                            <button
                                                                className='sysManager_bookings_iconActionButton'
                                                                type='button'
                                                                aria-label={`Edit ${room.name || 'booking room'}`}
                                                                title='Edit room'
                                                                onClick={() => openEditRoomModal(room)}
                                                            >
                                                                <FaPenToSquare size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </>
                )}

                {manageAccessState.open && (
                    <div
                        className='sysManager_bookings_accessModalOverlay'
                        role='presentation'
                        onClick={closeManageAccessModal}
                    >
                        <section
                            className='sysManager_bookings_accessModal'
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='sysManager-bookings-access-title'
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                className='sysManager_bookings_modalCloseButton'
                                type='button'
                                aria-label='Close access manager'
                                onClick={closeManageAccessModal}
                            >
                                ×
                            </button>
                            <div className='sysManager_bookings_accessHeader'>
                                <div>
                                    <h2
                                        className='sysManager_bookings_modalTitle'
                                        id='sysManager-bookings-access-title'
                                    >
                                        Staff Permissions
                                    </h2>
                                    <p className='sysManager_bookings_modalSubtitle'>
                                        Manage access for {manageAccessState?.group?.name || 'this group'}.
                                    </p>
                                </div>
                            </div>

                            <div className='sysManager_bookings_accessToolbar'>
                                <input
                                    className='sysManager_bookings_accessSearch'
                                    type='text'
                                    value={manageAccessState.searchText}
                                    placeholder='Search staff...'
                                    onChange={(event) => (
                                        setManageAccessState((currentValue) => ({
                                            ...currentValue,
                                            searchText: event.target.value,
                                        }))
                                    )}
                                />
                                <select
                                    className='sysManager_bookings_accessFilterSelect'
                                    value={manageAccessState.permissionFilter}
                                    onChange={(event) => (
                                        setManageAccessState((currentValue) => ({
                                            ...currentValue,
                                            permissionFilter: event.target.value,
                                        }))
                                    )}
                                >
                                    {STAFF_PERMISSION_FILTER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {selectedGroupAllowsAttendees && (
                                    <select
                                        className='sysManager_bookings_accessFilterSelect'
                                        value={manageAccessState.attendeesFilter}
                                        onChange={(event) => (
                                            setManageAccessState((currentValue) => ({
                                                ...currentValue,
                                                attendeesFilter: event.target.value,
                                            }))
                                        )}
                                    >
                                        {ATTENDEE_FILTER_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {filteredStaffAccessItems.length === 0 ? (
                                <div className='sysManager_bookings_emptyState'>
                                    No staff matched the current search and filter settings.
                                </div>
                            ) : (
                                <div className='sysManager_bookings_accessTableWrap'>
                                    <table className='sysManager_bookings_accessTable'>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Permission</th>
                                                {selectedGroupAllowsAttendees && <th>Allow Attendees</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStaffAccessItems.map((staffMember) => {
                                                const staffCogId = String(staffMember?.cogId || '').trim();
                                                const updatingState = updatingCogIds[staffCogId] || {};
                                                const isAccessUpdating = Boolean(updatingState.access);
                                                const isAttendeesUpdating = Boolean(updatingState.attendees);
                                                const currentAccessLevel = getGroupAccessLevel(
                                                    staffMember,
                                                    manageAccessState?.group?.groupId
                                                );
                                                const currentAllowAttendees = getGroupAllowAttendees(
                                                    staffMember,
                                                    manageAccessState?.group?.groupId
                                                );
                                                const canManageAttendees = currentAccessLevel !== 'none';
                                                const profileImageUrl = getProfileImageUrlWithSchoolFallback(
                                                    staffMember?.profileImageUrl,
                                                    userAppData?.schoolCode
                                                );

                                                return (
                                                    <tr key={staffCogId}>
                                                        <td>
                                                            <div className='sysManager_bookings_accessUserCell'>
                                                                {profileImageUrl ? (
                                                                    <div className='sysManager_bookings_accessAvatarWrap'>
                                                                        <div className='sysManager_bookings_accessAvatarPlaceholder'>
                                                                            {getAvatarInitial(staffMember?.displayName)}
                                                                        </div>
                                                                        <img
                                                                            className='sysManager_bookings_accessAvatar'
                                                                            src={profileImageUrl}
                                                                            alt={staffMember?.displayName || 'Staff member'}
                                                                            onError={(event) => {
                                                                                event.currentTarget.style.opacity = '0';
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className='sysManager_bookings_accessAvatarPlaceholder'>
                                                                        {getAvatarInitial(staffMember?.displayName)}
                                                                    </div>
                                                                )}
                                                                <div className='sysManager_bookings_accessUserText'>
                                                                    <strong>{staffMember?.displayName || 'Unknown user'}</strong>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {isAccessUpdating ? (
                                                                <div className='sysManager_bookings_accessSelectSpinner'>
                                                                    <CgSpinner className='sysManager_bookings_buttonSpinner' />
                                                                </div>
                                                            ) : (
                                                                <select
                                                                    className='sysManager_bookings_accessSelect'
                                                                    value={currentAccessLevel}
                                                                    onChange={(event) => (
                                                                        handleGroupAccessChange(staffMember, event.target.value)
                                                                    )}
                                                                >
                                                                    {GROUP_ACCESS_OPTIONS.map((option) => (
                                                                        <option key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                        </td>
                                                        {selectedGroupAllowsAttendees && (
                                                            <td>
                                                                {canManageAttendees && (
                                                                    <div className='sysManager_bookings_accessToggle'>
                                                                        <PolarSwitch
                                                                            data={currentAllowAttendees}
                                                                            loading={isAttendeesUpdating}
                                                                            disabled={false}
                                                                            setData={(nextValue) => (
                                                                                handleGroupAllowAttendeesChange(
                                                                                    staffMember,
                                                                                    nextValue
                                                                                )
                                                                            )}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                )}

                {editGroupState.open && (
                    <div
                        className='sysManager_bookings_accessModalOverlay'
                        role='presentation'
                        onClick={closeEditGroupModal}
                    >
                        <section
                            className='sysManager_bookings_editModal'
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='sysManager-bookings-edit-title'
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                className='sysManager_bookings_modalCloseButton'
                                type='button'
                                aria-label='Close group editor'
                                onClick={closeEditGroupModal}
                            >
                                ×
                            </button>
                            <div className='sysManager_bookings_accessHeader'>
                                <div>
                                    <h2
                                        className='sysManager_bookings_modalTitle'
                                        id='sysManager-bookings-edit-title'
                                    >
                                        Edit Group
                                    </h2>
                                    <p className='sysManager_bookings_modalSubtitle'>
                                        Update the group name and any existing display slot setting.
                                    </p>
                                </div>
                            </div>

                            <div className='sysManager_bookings_editBody'>
                                <label className='sysManager_bookings_editField'>
                                    <span className='sysManager_bookings_editLabel'>Name</span>
                                    <input
                                        className='sysManager_bookings_editInput'
                                        type='text'
                                        value={editGroupState.name}
                                        onChange={(event) => (
                                            setEditGroupState((currentValue) => ({
                                                ...currentValue,
                                                name: event.target.value,
                                            }))
                                        )}
                                    />
                                </label>

                                <label className='sysManager_bookings_editField'>
                                    <span className='sysManager_bookings_editLabel'>Type</span>
                                    <input
                                        className='sysManager_bookings_editInput'
                                        type='text'
                                        value={editGroupState.type}
                                        disabled
                                    />
                                </label>

                                {editGroupState.hasDisplaySlotMins && (
                                    <label className='sysManager_bookings_editField'>
                                        <span className='sysManager_bookings_editLabel'>Display Slot</span>
                                        <select
                                            className='sysManager_bookings_editInput'
                                            value={editGroupState.displaySlotMins}
                                            onChange={(event) => (
                                                setEditGroupState((currentValue) => ({
                                                    ...currentValue,
                                                    displaySlotMins: event.target.value,
                                                }))
                                            )}
                                        >
                                            {DISPLAY_SLOT_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                            </div>

                            <div className='sysManager_bookings_editActions'>
                                <button
                                    className='sysManager_bookings_editSecondaryButton'
                                    type='button'
                                    onClick={closeEditGroupModal}
                                    disabled={editGroupState.saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className='sysManager_bookings_editPrimaryButton'
                                    type='button'
                                    onClick={handleEditGroupSave}
                                    disabled={editGroupState.saving}
                                >
                                    {editGroupState.saving ? (
                                        <CgSpinner className='sysManager_bookings_buttonSpinner' />
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                            </div>
                        </section>
                    </div>
                )}

                {editSubGroupState.open && (
                    <div
                        className='sysManager_bookings_accessModalOverlay'
                        role='presentation'
                        onClick={closeEditSubGroupModal}
                    >
                        <section
                            className='sysManager_bookings_editModal'
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='sysManager-bookings-edit-subgroup-title'
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                className='sysManager_bookings_modalCloseButton'
                                type='button'
                                aria-label='Close sub group editor'
                                onClick={closeEditSubGroupModal}
                            >
                                ×
                            </button>
                            <div className='sysManager_bookings_accessHeader'>
                                <div>
                                    <h2
                                        className='sysManager_bookings_modalTitle'
                                        id='sysManager-bookings-edit-subgroup-title'
                                    >
                                        Edit Sub Group
                                    </h2>
                                    <p className='sysManager_bookings_modalSubtitle'>
                                        Update the sub group name.
                                    </p>
                                </div>
                            </div>

                            <div className='sysManager_bookings_editBody'>
                                <label className='sysManager_bookings_editField'>
                                    <span className='sysManager_bookings_editLabel'>Name</span>
                                    <input
                                        className='sysManager_bookings_editInput'
                                        type='text'
                                        value={editSubGroupState.name}
                                        onChange={(event) => (
                                            setEditSubGroupState((currentValue) => ({
                                                ...currentValue,
                                                name: event.target.value,
                                            }))
                                        )}
                                    />
                                </label>
                            </div>

                            <div className='sysManager_bookings_editActions'>
                                <button
                                    className='sysManager_bookings_editSecondaryButton'
                                    type='button'
                                    onClick={closeEditSubGroupModal}
                                    disabled={editSubGroupState.saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className='sysManager_bookings_editPrimaryButton'
                                    type='button'
                                    onClick={handleEditSubGroupSave}
                                    disabled={editSubGroupState.saving}
                                >
                                    {editSubGroupState.saving ? (
                                        <CgSpinner className='sysManager_bookings_buttonSpinner' />
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                            </div>
                        </section>
                    </div>
                )}

                {editRoomState.open && (
                    <div
                        className='sysManager_bookings_accessModalOverlay'
                        role='presentation'
                        onClick={closeEditRoomModal}
                    >
                        <section
                            className='sysManager_bookings_editModal'
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='sysManager-bookings-edit-room-title'
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                className='sysManager_bookings_modalCloseButton'
                                type='button'
                                aria-label='Close room editor'
                                onClick={closeEditRoomModal}
                            >
                                ×
                            </button>
                            <div className='sysManager_bookings_accessHeader'>
                                <div>
                                    <h2
                                        className='sysManager_bookings_modalTitle'
                                        id='sysManager-bookings-edit-room-title'
                                    >
                                        Edit Room
                                    </h2>
                                    <p className='sysManager_bookings_modalSubtitle'>
                                        Update room details and booking options.
                                    </p>
                                </div>
                            </div>

                            <div className='sysManager_bookings_editBody'>
                                <label className='sysManager_bookings_editField'>
                                    <span className='sysManager_bookings_editLabel'>Name</span>
                                    <input
                                        className='sysManager_bookings_editInput'
                                        type='text'
                                        value={editRoomState.name}
                                        onChange={(event) => (
                                            setEditRoomState((currentValue) => ({
                                                ...currentValue,
                                                name: event.target.value,
                                            }))
                                        )}
                                    />
                                </label>

                                <label className='sysManager_bookings_editField'>
                                    <span className='sysManager_bookings_editLabel'>Group</span>
                                    <Select
                                        className='sysManager_bookings_editSelect'
                                        classNamePrefix='sysmanager-bookings-select'
                                        value={editRoomState.groupOption}
                                        options={groupSelectOptions}
                                        onChange={(selectedOption) => (
                                            setEditRoomState((currentValue) => ({
                                                ...currentValue,
                                                groupOption: selectedOption,
                                            }))
                                        )}
                                        isDisabled={editRoomState.saving}
                                    />
                                </label>

                                <label className='sysManager_bookings_editField'>
                                    <span className='sysManager_bookings_editLabel'>Sub Group</span>
                                    <Select
                                        className='sysManager_bookings_editSelect'
                                        classNamePrefix='sysmanager-bookings-select'
                                        value={editRoomState.subGroupOption}
                                        options={subGroupSelectOptions}
                                        onChange={(selectedOption) => (
                                            setEditRoomState((currentValue) => ({
                                                ...currentValue,
                                                subGroupOption: selectedOption || subGroupSelectOptions[0] || null,
                                            }))
                                        )}
                                        isClearable={false}
                                        isDisabled={editRoomState.saving}
                                    />
                                </label>

                                <label className='sysManager_bookings_editField'>
                                    <span className='sysManager_bookings_editLabel'>Notes</span>
                                    <textarea
                                        className='sysManager_bookings_editTextarea'
                                        value={editRoomState.notes}
                                        onChange={(event) => (
                                            setEditRoomState((currentValue) => ({
                                                ...currentValue,
                                                notes: event.target.value,
                                            }))
                                        )}
                                    />
                                </label>

                                <div className='sysManager_bookings_toggleGrid'>
                                    <div className='sysManager_bookings_toggleCard'>
                                        <span className='sysManager_bookings_editLabel'>Bookable</span>
                                        <PolarSwitch
                                            data={editRoomState.bookable}
                                            disabled={editRoomState.saving}
                                            setData={(nextValue) => (
                                                setEditRoomState((currentValue) => ({
                                                    ...currentValue,
                                                    bookable: nextValue,
                                                }))
                                            )}
                                        />
                                    </div>
                                    <div className='sysManager_bookings_toggleCard'>
                                        <span className='sysManager_bookings_editLabel'>Allow Notes</span>
                                        <PolarSwitch
                                            data={editRoomState.allowNotes}
                                            disabled={editRoomState.saving}
                                            setData={(nextValue) => (
                                                setEditRoomState((currentValue) => ({
                                                    ...currentValue,
                                                    allowNotes: nextValue,
                                                }))
                                            )}
                                        />
                                    </div>
                                    <div className='sysManager_bookings_toggleCard'>
                                        <span className='sysManager_bookings_editLabel'>Allow Attendees</span>
                                        <PolarSwitch
                                            data={editRoomState.allowAttendees}
                                            disabled={editRoomState.saving}
                                            setData={(nextValue) => (
                                                setEditRoomState((currentValue) => ({
                                                    ...currentValue,
                                                    allowAttendees: nextValue,
                                                }))
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className='sysManager_bookings_editActions'>
                                <button
                                    className='sysManager_bookings_editSecondaryButton'
                                    type='button'
                                    onClick={closeEditRoomModal}
                                    disabled={editRoomState.saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className='sysManager_bookings_editPrimaryButton'
                                    type='button'
                                    onClick={handleEditRoomSave}
                                    disabled={editRoomState.saving}
                                >
                                    {editRoomState.saving ? (
                                        <CgSpinner className='sysManager_bookings_buttonSpinner' />
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}
